/**
 * PaintaFloor calculator:
 * - Inputs: total surface (m²), losses (%)
 * - Formula: ceil((surface + surface * losses%) / coveragePerBox)
 */
(function () {
  const DATA = {
    trigger: 'data-paintafloor-calc-trigger',
    results: 'data-paintafloor-results',
    boxes: 'data-paintafloor-boxes-value',
    heading: 'data-paintafloor-result-heading',
  };

  class PaintaFloorCalculator extends HTMLElement {
    connectedCallback() {
      const trigger = this.querySelector(`[${DATA.trigger}]`);
      const surfaceInput = this.querySelector('input[name="paintafloor_area"]');
      const lossInput = this.querySelector('input[name="paintafloor_loss"]');

      if (!(surfaceInput instanceof HTMLInputElement)) return;
      if (!(lossInput instanceof HTMLInputElement)) return;
      if (!(trigger instanceof HTMLElement)) return;

      this.#applyDefaults(lossInput);

      this.addEventListener(
        'input',
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement)) return;
          if (target.type !== 'number') return;
          this.#enforceNumberConstraints(target);
        },
        true
      );

      this.addEventListener(
        'keydown',
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement)) return;
          if (target.type !== 'number') return;

          const allowedKeys = [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
            'Enter'
          ];

          if (allowedKeys.includes(event.key)) return;

          const isNumberKey = event.key >= '0' && event.key <= '9';
          const isDecimalSeparator = event.key === '.' || event.key === ',';

          if (!isNumberKey && !isDecimalSeparator) {
            event.preventDefault();
          }
        },
        true
      );

      trigger.addEventListener('click', () => {
        const resultHeading = this.querySelector(`[${DATA.heading}]`);
        const result = this.#calculate(surfaceInput, lossInput);
        this.#renderResult(result);

        if (resultHeading instanceof HTMLElement) {
          requestAnimationFrame(() => {
            resultHeading.focus();
            resultHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      });
    }

    /**
     * @param {HTMLInputElement} lossInput
     */
    #applyDefaults(lossInput) {
      const defaultLoss = parseFloat(this.dataset.defaultLoss || '10');
      if (Number.isFinite(defaultLoss) && lossInput.value.trim() === '') {
        lossInput.value = String(defaultLoss);
      }
    }

    /**
     * @param {HTMLInputElement} surfaceInput
     * @param {HTMLInputElement} lossInput
     */
    #calculate(surfaceInput, lossInput) {
      const rawArea = parseFloat(surfaceInput.value);
      const rawWastage = parseFloat(lossInput.value);

      const area = Number.isFinite(rawArea) && rawArea > 0 ? rawArea : 0;
      const wastagePercent = Number.isFinite(rawWastage) && rawWastage >= 0 ? rawWastage : 0;

      if (area === 0) {
        return { valid: false, boxes: 0 };
      }

      const wastage = wastagePercent / 100;
      const boxes = Math.ceil((area + area * wastage) / 2.2);

      return { valid: true, boxes };
    }

    /**
     * @param {{ valid: boolean; boxes: number }} result
     */
    #renderResult(result) {
      const results = this.querySelector(`[${DATA.results}]`);
      const boxesValue = this.querySelector(`[${DATA.boxes}]`);
      if (!(results instanceof HTMLElement)) return;
      if (!(boxesValue instanceof HTMLElement)) return;

      if (!result.valid) {
        boxesValue.textContent = this.dataset.invalidSurfaceLabel || 'Please enter a surface greater than 0.';
        results.hidden = false;
        return;
      }

      boxesValue.textContent = String(result.boxes);
      results.hidden = false;
    }

    /**
     * @param {HTMLInputElement} input
     */
    #enforceNumberConstraints(input) {
      const raw = input.value;
      if (raw === '' || raw === '-' || raw === '+') return;

      let value = parseFloat(raw);
      if (!Number.isFinite(value)) {
        input.value = '';
        return;
      }

      const minAttr = input.getAttribute('min');
      if (minAttr !== null && minAttr !== '') {
        const min = Number(minAttr);
        if (Number.isFinite(min) && value < min) {
          value = min;
        }
      }

      const maxAttr = input.getAttribute('max');
      if (maxAttr !== null && maxAttr !== '') {
        const max = Number(maxAttr);
        if (Number.isFinite(max) && value > max) {
          value = max;
        }
      }

      input.value = String(value);
    }
  }

  if (!customElements.get('paintafloor-calculator')) {
    customElements.define('paintafloor-calculator', PaintaFloorCalculator);
  }
})();
